import { displayNameMake } from '@javascript/instant_search/carwash/makes';
import { displayNameModel } from '@javascript/instant_search/carwash/models';
import type { RelatedRefinementList } from '@sharedTypes/InstantSearch';

export type ParsedMakeModelQs = Record<
  string,
  ['_all'] | (string | Record<string, string[]>)[]
>;

export type LegacyMakeModelParams = {
  make: string;
  model?: string;
  trim?: string;
};

export function isLegacyMakeModelParams(
  input: ParsedMakeModelQs | LegacyMakeModelParams
): input is LegacyMakeModelParams {
  return 'make' in input && 'model' in input;
}

export function legacyMakeModelParser(input: LegacyMakeModelParams) {
  const displayMake = displayNameMake(input.make);

  const makeModelFilterProps = {
    'makeModel.make': [displayMake],
    'makeModel.model': [],
    'makeModel.variant': [],
  };

  if (!input.model || input.model === '_all') {
    return makeModelFilterProps;
  }

  const displayModel = displayNameModel(input.model);

  return {
    'makeModel.make': [displayMake],
    'makeModel.model': [`${displayMake} > ${displayModel}`],
    'makeModel.variant': input.trim
      ? [`${displayMake} > ${displayNameModel(input.model)} > ${input.trim}`]
      : [],
  };
}

export function makeModelParser(
  input?: ParsedMakeModelQs | LegacyMakeModelParams
): RelatedRefinementList {
  if (!input)
    return {
      'makeModel.make': [],
      'makeModel.model': [],
      'makeModel.variant': [],
    };

  if (isLegacyMakeModelParams(input)) {
    return legacyMakeModelParser(input);
  }

  const result: {
    make: string[];
    models: string[];
    variants: string[];
  } = { make: [], models: [], variants: [] };

  // eslint-disable-next-line guard-for-in
  for (const make in input) {
    const displayMake = displayNameMake(make);
    result.make.push(displayMake);

    const modelsAndVariants = input[make];

    if (!modelsAndVariants.includes('_all')) {
      for (const modelOrVariant of modelsAndVariants) {
        if (typeof modelOrVariant === 'string') {
          result.models.push(
            `${displayMake} > ${displayNameModel(modelOrVariant)}`
          );
        } else if (typeof modelOrVariant === 'object') {
          for (const [model, variantList] of Object.entries(modelOrVariant)) {
            const displayModel = displayNameModel(model);
            result.models.push(`${displayMake} > ${displayModel}`);
            for (const variant of variantList) {
              result.variants.push(
                `${displayMake} > ${displayModel} > ${variant}`
              );
            }
          }
        }
      }
    }
  }

  return {
    'makeModel.make': result.make,
    'makeModel.model': result.models,
    'makeModel.variant': result.variants,
  };
}

export function booleanParser(urlBoolean: string | undefined): boolean {
  if (urlBoolean === 'true') return true;
  return false;
}

export const singleSelectNumericParser = (
  numericMenuValue: string | undefined,
  overOrUnder: 'exact' | 'over' | 'under' = 'under'
) => {
  if (numericMenuValue === undefined) return;
  if (overOrUnder === 'exact') return numericMenuValue;
  return overOrUnder === 'over'
    ? `${numericMenuValue}:`
    : `:${numericMenuValue}`;
};

export const searchTypeParser = (searchType?: string) => {
  //remove Cars from the searchType
  if (!searchType) return undefined;
  return searchType.replace(' Cars', '');
};

const sortOrderMap: Record<string, string> = {
  monthly_payment_up: 'monthly_asc',
  monthly_payment_down: 'monthly_dsc',
  price_up: 'cash_asc',
  price_down: 'cash_dsc',
  mileage: 'mileage',
  age: 'age',
  recently_added: 'recentlyadded',
  distance: 'geolocation',
};

export const sortOrderParser = (
  sortOrder: string | undefined,
  indexName: string
) => {
  if (!sortOrder) return undefined;
  return indexName + `_${sortOrderMap[sortOrder] || sortOrder}`;
};

export const registeredAtParser = (
  dorParam?: string | null
): string | undefined => {
  if (!dorParam || isNaN(Number(dorParam))) return undefined;

  const dorMonths = Number(dorParam);
  const now = new Date();
  const targetMonth = now.getMonth() - dorMonths;

  const date = new Date(Date.UTC(now.getFullYear(), targetMonth, 1));
  const daysInTargetMonth = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const day = Math.min(now.getUTCDate(), daysInTargetMonth);

  date.setUTCDate(day);
  date.setUTCHours(0, 0, 0, 0);

  return `${Math.floor(date.getTime() / 1000)}:`;
};
